import {useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'

const MAX_MAIN_ROWS = 3

/** Number of errors/failed records for a main table row, regardless of API field naming. */
export const getRowErrorCount = row => Number(row?.errorCount ?? row?.failedRecords ?? 0)

/** Identifier used to correlate a main row with its sub-rows (failed records). */
export const getSubRowParentId = row => row?.fileUploadUUID ?? row?.id

/** Combines the error column/message pair into the "STATUS (REMARK)" display value. */
export const formatErrorRemark = rowData => {
  const errorCol = rowData?.errorCol ?? ''
  const errorMsg = rowData?.errorMsg ?? rowData?.errorMessage ?? ''
  return `${errorCol}  ${errorMsg}`
}

/**
 * Limits the main table to a preview subset and determines whether any row has
 * errors, in which case the table should render as expandable.
 */
export const useUploadHistoryMainRows = (mainData, maxRows = MAX_MAIN_ROWS) => {
  const mainDataSubset = useMemo(() => (Array.isArray(mainData) ? mainData.slice(0, maxRows) : []), [mainData, maxRows])

  const tableHasExpandableRows = useMemo(() => mainDataSubset.some(row => getRowErrorCount(row) > 0), [mainDataSubset])

  return {mainDataSubset, tableHasExpandableRows}
}

/**
 * Fetches a file's sub-rows (failed records) and derives default columns for them
 * when none are explicitly provided.
 */
export const useUploadHistorySubRows = (parentId, fetchSubRows) => {
  const {data = [], isFetching} = useQuery({
    queryKey: ['inventory-upload-errors', parentId, fetchSubRows],
    queryFn: () => (fetchSubRows ? fetchSubRows(parentId) : []),
    enabled: !!fetchSubRows && !!parentId,
  })

  const rows = Array.isArray(data) ? data : (data?.data ?? [])
  const hasWarehouseId = rows.some(row => !!row.warehouseId)

  const defaultSubColumns = useMemo(
    () => [
      {columnKey: 'productId', header: 'Product ID'},
      ...(hasWarehouseId ? [{columnKey: 'warehouseId', header: 'Warehouse ID'}] : []),
      {columnKey: 'errorMsg', header: 'STATUS (REMARK)'},
    ],
    [hasWarehouseId]
  )

  return {rows, isFetching, defaultSubColumns}
}
